import { AfterViewInit, Component, ElementRef, NgZone, OnDestroy, OnInit, TemplateRef, ViewChild, ViewEncapsulation } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { LetterDialogTypes } from 'countable@helpers';
import { TemplateDialogModel, UserDetailModel, UserTimeZoneModel } from 'countable@model';
import moment from 'moment';
import { CookieService } from 'ngx-cookie-service';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { tap } from 'rxjs/operators';
import { FirmModel } from 'src/app/model/firm/firm.model';
import { TwoFactorResponseModel } from 'src/app/model/two-factor.model';
import { UserModel } from 'src/app/model/user.model';
import { MfaService } from 'src/app/services/mfa.service';
import { TimezoneService } from 'src/app/services/timezone.service';
import { environment } from 'src/environments/environment';
import { BillingModel } from '../../../model/billing/billing.model';
import { OauthKickOffModel } from '../../../model/oauth/oauth-kick-off.model';
import { AuthService } from '../../../services/auth.service';
import { BillingService } from '../../../services/billing.service';
import { CaptchaService } from '../../../services/captcha.service';
import { FirmService } from '../../../services/firm.service';
import { IntegrationService, OauthConnectionType } from '../../../services/integration.service';
import { CommonDialogComponent } from '../../Engagements/NTR_Dashboard/common-dialog/common-dialog.component';
import { CountableDialogComponent } from '../../remote-shared/countable-dialog/countable-dialog.component';
import { SharedService } from '../../shared/shared.service';
import Intercom from '@intercom/messenger-js-sdk';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false
})
export class LoginComponent implements OnInit, OnDestroy, AfterViewInit {

  @ViewChild('googleSSO') googleSSODiv: ElementRef<HTMLElement>;
  @ViewChild('loginProblemForNormalUsers') loginProblemForNormalUsersDialog: TemplateRef<any>;

  loginForm: UntypedFormGroup;
  token: any;
  urlStr: string;
  userData: UserDetailModel;
  billing: BillingModel;
  hide = true;
  imagePath = 'bg-0.jpg';
  isLoginWindow = true;
  isDashboardLoading = false;
  isLoginInProgress = false;
  code: number;
  public isMfaWindow: boolean = false;
  public user: UserModel;
  public mfaTypeData: TwoFactorResponseModel | null = new TwoFactorResponseModel();
  public otpForm: UntypedFormGroup;
  public isValidOtp: boolean = false;
  public isInvalidOtp: boolean = false;
  public mandateMFA: boolean = false;
  public isVerified: boolean = false;
  public resendTimer: number = -1;
  // public isDeploymentDone: boolean = false;
  isAccountPaused = false;
  firm: FirmModel;
  userSelectedTimeZone: UserTimeZoneModel;
  private redirectCode: string = null;
  private readonly bgImageInterval: any;
  private resendTimerSub: Subscription = new Subscription();
  private sourceOfLogin: 'sso' | 'platform';
  private subscriptions: Subscription = new Subscription();

  constructor(private cookie: CookieService, private sharedService: SharedService, private fb: UntypedFormBuilder,
    private router: Router, private route: ActivatedRoute, private toaster: ToastrService,
    private authService: AuthService, private integrationService: IntegrationService,
    private mfaService: MfaService, private captchaService: CaptchaService, private ngZone: NgZone,
    private billingService: BillingService, private dialog: MatDialog, private firmService: FirmService,
    private timezoneService: TimezoneService) {
    this.sharedService.mandatorymfa.next(false);
    this.route.queryParamMap.subscribe(async params => {
      if (params.has('error')) {
        if (params.get('error') == 'invalid_request') {
          this.toaster.error('Invalid request, please try again');
        }
        if (params.get('error') == 'locked_account') {
          this.toaster.error('User is not active, please contact support');
        }
        if (params.get('error') == 'access_denied') {
          this.toaster.error('Access Denied');
        }
        if (params.get('error') == 'non_existing_account') {
          if (params.has('redirect')) {
            return this.router.navigate(['./signup', params.get('redirect')]);
          } else {
            this.toaster.error('Account does not exist, please signup first');
          }
        }
        if (params.get('error') == 'unverified_account_qb') {
          this.toaster.error('Please <a class="text-link" href="https://accounts.intuit.com/app/account-manager/security">login to your intuit account</a>',
            'Your email is not yet verified', {
            enableHtml: true,
            disableTimeOut: true
          });
        }
        this.router.navigate(['./login']).then();
      }
      if (params.has('code')) {
        this.isDashboardLoading = true;
        this.isLoginInProgress = true; // we just put the window in wait until captcha is loaded!
        if (params.has('redirect')) {
          this.redirectCode = params.get('redirect');
        }
        setTimeout(() => {
          this.loginWithOAuth(params.get('code'));
        }, 1000);
      }
      if (params.has('token')) {
        this.token = params.get('token');
        if (this.token) { // we need to delete this in the future
          this.urlStr = this.token.replace(/ /g, '+');
        }
      }
    });
    this.route.params.subscribe(params => {
      if (params.type && params.type === 'disconnect') {
        this.router.navigate(['./login'])
          .then(() => this.toaster.success('QuickBooks SSO is disconnected'));
      } else if (params.type) {
        if (params.type !== 'google') {
          this.isLoginInProgress = true;
          this.loginWithOAuthKickOff(params.type.toLowerCase());
        }
      }
    });
    this.bgImageInterval = setInterval(() => {
      this.imagePath = 'bg-' + Math.floor(Math.random() * 2) + '.jpg';
    }, 5000);
  }

  ngAfterViewInit(): void {
    this.captchaService.showCaptchaBanner().then();
    this.loadGoogle();
  }

  ngOnDestroy() {
    this.isDashboardLoading = false;
    this.captchaService.hideCaptchaBanner().then();
    this.sharedService.loginForms = false;
    clearInterval(this.bgImageInterval);
    this.resendTimerSub.unsubscribe();
    this.subscriptions && this.subscriptions.unsubscribe();
    this.isLoginInProgress = false;
  }

  ngOnInit() {
    this.authService.cleanTheStorage();
    this.loginForm = this.fb.group({
      emailId: ['', [Validators.required, Validators.pattern(environment.EMAIL_REG_EXP)]],
      password: ['', Validators.required],
      rememberMe: ''
    });
    this.createOtpForm();
    // Remember me auto login
    if (this.cookie.check('email') && this.cookie.check('pass')) {
      this.loginForm.patchValue({
        'emailId': window.atob(this.cookie.get('email')),
        'password': window.atob(this.cookie.get('pass')),
        'rememberMe': true
      });
      this.login();
    }
    this.sharedService.loginForms = true;
    this.subscriptions.add(
      this.firmService.subject.subscribe(firm => {
        if (firm) {
          this.firm = firm;
        }
      })
    );
    this.subscriptions.add(
      this.timezoneService.subject.subscribe(data => {
        this.userSelectedTimeZone = data;
      })
    );
  }

  goToLogin() {
    this.router.navigate(['/login']).then(() => {
      this.isLoginWindow = true;
      this.isMfaWindow = false;
      this.isLoginInProgress = false;
      this.isDashboardLoading = false;
    }).finally(() => {
      this.loadGoogle();
    });
  }

  createOtpForm() {
    this.otpForm = this.fb.group({
      'otpNumber': new UntypedFormControl('')
    });
  }

  getAuthList() {
    this.mfaService.FetchAuthList().subscribe(resp => {
      let authTypeObj: TwoFactorResponseModel = null;
      resp && resp.forEach(l => {
        if ((l.hasOwnProperty('isFirmMandatoryMfa') && l.hasOwnProperty('isMfaEnabledForUser')) && (Object.keys(l).length === 2)) {
          this.sharedService.mandatorymfa.next(l.isFirmMandatoryMfa);
        }
        if (l.hasOwnProperty('emailCodeExpiry') && l.hasOwnProperty('qrCodeString')) {
          authTypeObj = { ...l };
        }
      });
      this.mfaTypeData = authTypeObj ? { ...authTypeObj } : null;
      if (this.mfaTypeData && this.mfaTypeData.isActive) {
        this.isMfaWindow = true;
        this.isLoginWindow = false;
        this.sharedService.mandatorymfa.next(false);
        this.mfaTypeData.type === 'EMAIL' && this.getEmailAuthentication(false);
      } else {
        this.mandateMFA = true;
        this.getUserDetail();
      }
    });
  }

  getEmailAuthentication(isResend: boolean) {
    if (isResend && this.resendTimer !== -1) {
      return;
    }
    this.mfaService.fetchEmailAuthentication().subscribe().add(() => {
      this.resendEmailOtp();
    });
  }

  verifyOTP(event) {
    const otp = event.target.value.replace(/[^0-9]/g, '');
    if (otp.length <= 6) {
      this.otpForm.setValue({ 'otpNumber': otp });
      this.isValidOtp = otp.length === 6 && this.otpForm.valid;
    }
  }

  verifyCode() {
    const otp = this.otpForm.get('otpNumber').value;
    const type = this.mfaTypeData.type === 'GOOGLE_AUTHENTICATOR' ? 'google' : 'email';
    switch (type) {
      case ('google'):
        this.getGoogleOtpVerification(otp);
        break;
      case ('email'):
        this.getEmailOtpVerification(otp);
        break;
    }
  }

  getGoogleOtpVerification(code: number) {
    this.mfaService.fetchGoogleOtpStatus(code).pipe(
      tap(resp => {
        if (resp) {
          this.isMfaWindow = false;
          this.isLoginInProgress = true;
          this.isDashboardLoading = true;
          this.isInvalidOtp = false;
          this.isVerified = true;
          this.authService.setUser(this.user);
          this.getUserDetail();
        } else {
          this.isInvalidOtp = true;
        }
      })
    ).subscribe().add(() => {
      this.isValidOtp = false;
      this.otpForm.reset();
    });
  }

  getEmailOtpVerification(code: number) {
    this.mfaService.fetchEmailOtpStatus(code).pipe(
      tap(resp => {
        this.mfaService.resetTimer();
        this.resendTimerSub.unsubscribe();
        if (resp.success && resp.success === true) {
          this.isMfaWindow = false;
          this.isLoginInProgress = true;
          this.isDashboardLoading = true;
          this.isInvalidOtp = false;
          this.isVerified = true;
          this.authService.setUser(this.user);
          this.getUserDetail();
        } else if (!resp.success) {
          this.isInvalidOtp = true;
          // this.resendEmailOtp();
        }
      })
    ).subscribe().add(() => {
      this.isValidOtp = false;
      this.otpForm.reset();
    });
  }

  resendEmailOtp() {
    this.resendTimerSub = this.mfaService.resendCodeTimer().subscribe({
      next: (resp) => {
        this.resendTimer = resp;
      },
      error: (err) => console.log(err),
      complete: () => {
        this.resendTimer = -1;
        this.resendTimerSub.unsubscribe();
      }
    });
  }

  login() {
    if (!this.loginForm.valid) {
      return;
    }
    this.isLoginInProgress = true;
    // to clean up the storages before a new person is login to prevent mixing up the data in the cache
    localStorage.clear();
    sessionStorage.clear();
    this.closeErrorToaster();
    try {
      this.captchaService.getCaptchaToken('login').then((token) => {
        const loginForm = this.loginForm.value;
        this.authService.login({
          'username': loginForm.emailId,
          'password': loginForm.password,
          'captcha': token
        }).subscribe({
          next: res => {
            if (res.status === 200) {
              if (loginForm.rememberMe) {
                this.cookie.set('email', window.btoa(loginForm.emailId), 1, '/login', '', true, 'Lax');
                this.cookie.set('pass', window.btoa(loginForm.password), 1, '/login', '', true, 'Lax');
              }
              this.user = res.data;
              this.authService.setUser(res.data);
              if (res.data.isMfaEnable) {
                this.getAuthList();
              } else {
                this.isDashboardLoading = true;
                this.getUserDetail();;
              }
            } else {
              this.isLoginInProgress = false;
              res.errors.forEach(error => {
                this.toaster.error(error);
              });
            }
          }, error: () => {
            this.toaster.error('Could not login at this time. please contact support');
            this.isLoginInProgress = false;
          }
        });
      });
    } catch (e) {
      this.toaster.error('Could not login at this time. please contact support');
      this.isLoginInProgress = false;
      this.isDashboardLoading = false;
    }
  }

  loginWithOAuthKickOff(type: string) {
    this.isLoginInProgress = true;
    this.isDashboardLoading = true;
    try {
      this.captchaService.getCaptchaToken('sso_login_' + type).then((captcha) => {
        const data: OauthKickOffModel = {
          redirectMode: OauthConnectionType.LOGIN, // enum in IntegrationOauthStateEntity
          key: type,
          captcha: captcha
        };
        try {
          this.integrationService.oauthProvidersKickOff(data);
        } catch (e) {
          this.isLoginInProgress = false;
          this.isDashboardLoading = false;
          this.toaster.error('Could not initiate the login process');
        }
      });
    } catch (e) {
      this.isLoginInProgress = false;
      this.isDashboardLoading = false;
      console.log(e);
    }
  }

  openTermsPrivacyDialog(type: string) {
    this.sharedService.termsPrivacyShow.next(type);
  }

  siteNavigate() {
    window.open('https://countable.co/', '_self');
  }

  // Pause Account or Is Trial Expired or switch firms
  commonDialog(dialogType, expiryIn) {
    const config: MatDialogConfig = {
      data: {
        dialogType: dialogType,
        dialogHeader: '',
        data: expiryIn
      },
      disableClose: false,
      id: dialogType
    };
    this.sharedService.dialogService.open(CommonDialogComponent, config, dialogType).subscribe();
  }

  closeErrorToaster() {
    this.isAccountPaused = false;
  }

  // checking peg/pact expiring in 1 month or 3 days
  checkingPegPactExpiringOrNot() {
    const currenDate = moment().startOf('day');
    const pegPactExpiryDate = moment(this.firm.pegPacExpiryDate, 'YYYY-MM-DD');
    if (moment.duration(pegPactExpiryDate.diff(currenDate)).asDays() === 0 || moment.duration(pegPactExpiryDate.diff(currenDate)).asDays() === 3 ||
      moment.duration(pegPactExpiryDate.diff(currenDate)).asDays() === 30) {
      const days = moment.duration(pegPactExpiryDate.diff(currenDate)).asDays() === 0 ? 'Your PEG/PACT expires today' :
        (moment.duration(pegPactExpiryDate.diff(currenDate)).asDays() === 3 ? 'Your PEG/PACT is about to expire in 3 days' : 'Your PEG/PACT is about to expire in 1 month');
      this.commonDialog('pegPactExpiresInMonthOrDays', days);
    }
  }

  closeDialogAndShowLogin(): void {
    this.isLoginWindow = true;
    this.isMfaWindow = false;
    this.isLoginInProgress = false;
    this.isDashboardLoading = false;
  }

  private loginWithGoogle(token: string) {
    try {
      this.isDashboardLoading = true;
      this.isLoginInProgress = true;
      this.captchaService.getCaptchaToken('sso_login_google').then((captcha) => {
        this.authService.loginWithGoogle({ token: token, captcha: captcha }).subscribe({
          next: res => {
            if (res.status === 200) {
              this.authService.setUser(res.data);
              this.getUserDetail('sso');
            } else {
              this.isLoginInProgress = false;
              this.isDashboardLoading = false;
              res.errors.forEach((error) => {
                this.toaster.error(error);
              });
            }
          },
          error: () => {
            this.isLoginInProgress = false;
            this.isDashboardLoading = false;
            this.toaster.error('Authentication failed');
          }
        });
      });
    } catch (e) {
      this.isLoginInProgress = false;
      this.isDashboardLoading = false;
      console.log(e);
    }
  }

  private loginWithOAuth(code: string) {
    this.isDashboardLoading = true;
    try {
      this.captchaService.getCaptchaToken('sso_login_oauth').then((captcha) => {
        this.authService.loginWithSso(code, captcha).subscribe({
          next: res => {
            if (res.status === 200) {
              this.authService.setUser(res.data);
              this.getUserDetail('sso');
            } else {
              this.router.navigate(['./']).then(() => {
                this.isLoginInProgress = false;
                this.isDashboardLoading = false;
                res.errors.forEach((error) => {
                  this.toaster.error(error);
                });
              });
            }
          }, error: () => {
            this.router.navigate(['./']).then();
            this.isLoginInProgress = false;
            this.isDashboardLoading = false;
            this.toaster.error('Could not verify the SSO credentials, please try again');
          }
        });
      });
    } catch (e) {
      console.log(e);
      this.isLoginInProgress = false;
      this.isDashboardLoading = false;
      this.toaster.error('Could not fulfill the SSO request, please try again');
    } finally {
      this.loadGoogle();
    }
  }

  private getUserDetail(from: 'sso' | 'platform' = 'platform') {
    this.authService.fetchUserProfile().subscribe({
      next: res => {
        this.sourceOfLogin = from;
        if (res.status === 200) {
          this.userData = res.data;
          this.authService.setUserDetail(this.userData);
          this.authService.authenticated(true);
          this.checkFirmAndBilling();
          this.integrationService.getAccountingIntegrationsBasicInfo().subscribe();
          this.getToken()
        } else {
          this.toaster.error('Could not load the user data, please contact support');
          this.isLoginInProgress = false;
          this.isDashboardLoading = false;
        }
      },
      error: () => {
        this.toaster.error('Could not load the user data, please try again later');
        this.isLoginInProgress = false;
        this.isDashboardLoading = false;
      }
    });
  }

  private checkFirmAndBilling(): void {
    try {
      this.firmService.getFirm().subscribe({
        next: firm => {
          this.firm = firm;
          this.billingService.refreshBilling(true).subscribe({
            next: billing => {
              if (this.userData.useraccesstypeid === 2 || this.userData.isbillingenabled) { // admin
                if (this.firm?.isAccountLockedForBilling || this.firm?.isPaused || (billing?.isOnTrial && billing?.isTrialExpired) || billing?.isCanceled) {
                  this.router.navigate(firm.onboarded ? ['/dashboard/members/settings/billing'] : ['/firm']).then();
                  return;
                }
              } else { // not admin
                if (this.firm?.isAccountLockedForBilling || this.firm.isPaused || (billing?.isOnTrial && billing?.isTrialExpired) || billing?.isCanceled) {
                  this.dialog.open(this.loginProblemForNormalUsersDialog, { disableClose: true }).afterClosed().subscribe(() => {
                    this.closeDialogAndShowLogin();
                  });
                  return;
                }
              }
              // if no problem found
              if (this.redirectCode) {
                this.router.navigate(['/dashboard/members/settings/sso-provider']).then();
              } else {
                this.redirectUserAfterLogin(false);
              }
            }
          });
        }
      });
    } catch (e) {
      this.toaster.error('Could not load the user data, please contact support error: [' + new Date().getMilliseconds() + ']');
      this.isLoginWindow = true;
      this.isLoginInProgress = false;
      this.isDashboardLoading = false;
      this.authService.logout();
    }
  }

  private redirectUserAfterLogin(isAuthenticated: boolean) {
    // this.npsService.checkSurveyStatus(); // NPS survey is not needed for now disabling it R36 as per ATIN CPT-12652
    if (this.userData.userroleId == 4) {
      this.router.navigate(this.userData.firmprocessstatus ? ['/client-account-setup'] : ['/dashboard/clients/engagements']).then();
      return;
    }
    if (this.sourceOfLogin && this.sourceOfLogin === 'platform') {
      if ((!isAuthenticated && !this.user.isMfaEnable) || isAuthenticated || this.isVerified) {
        this.router.navigate(['/dashboard/members/Dashboard']).then();
        if (this.firm.isPegPacMandatory && this.firm.isPegPacValid) {
          this.checkingPegPactExpiringOrNot();
        }
        this.checkTimeZone();
      } else if (!isAuthenticated && this.mandateMFA) {
        this.sharedService.mandatorymfa.next(true);
        this.router.navigate(['/dashboard/members/settings/privacy-and-security']).then();
      }
    } else if (this.sourceOfLogin && this.sourceOfLogin === 'sso') {
      this.router.navigate(['/dashboard/members/Dashboard']).then();
      if (this.firm.isPegPacMandatory && this.firm.isPegPacValid) {
        this.checkingPegPactExpiringOrNot();
      }
      this.checkTimeZone();
    }
  }

  private loadGoogle() {
    if (typeof google == 'undefined' || !this.googleSSODiv || !this.googleSSODiv.nativeElement) {
      setTimeout(() => {
        this.loadGoogle();
      }, 100);
      return;
    }
    // @ts-ignore
    google.accounts.id.initialize({
      client_id: environment.GOOGLE_CLIENT_ID,
      callback: ({ credential }) => {
        this.ngZone.run(() => {
          this.loginWithGoogle(credential);
        });
      }
    });
    // @ts-ignore
    google.accounts.id.renderButton(
      this.googleSSODiv.nativeElement,
      {
        theme: 'outline',
        size: 'large',
        type: 'label',
        shape: 'rectangular',
        text: 'Sign in with Google',
        logo_alignment: 'center'
      }
    );
    if (!this.isLoginInProgress) {
      // @ts-ignore
      google.accounts.id.prompt(); // also display the One Tap dialog
    }
  }

  // checking system time zone and application time zone
  private checkTimeZone() {
    if(!this.firm.onboarded){
      return;
    }
    try {
      this.timezoneService.shouldWeShowTheDialog().subscribe(show => {
        if (show) {
          this.timezoneService.getUserTimeZone().subscribe(userTimeZone => {
            if (userTimeZone && userTimeZone.status === 200 && !(this.timezoneService.checkingSystemAndAppTimeZone() === userTimeZone.data.utcValue)) {
              const config: MatDialogConfig<Partial<TemplateDialogModel>> = {
                data: {
                  dialogType: LetterDialogTypes.TIME_ZONE,
                  timeZoneData: userTimeZone?.data
                },
                disableClose: true,
                id: 'mismatch_time_zone'
              };
              this.dialog.open(CountableDialogComponent, config).afterClosed().subscribe(result => {
                if (result) {
                  this.router.navigate(['/dashboard/members/settings/myaccount']).then(() => {
                    this.timezoneService.markTheDialog('DONE');
                  });
                } else {
                  this.timezoneService.markTheDialog('IGNORE');
                }
              });
            }
          });
        }
      });
    } catch (e) {
      console.error('TimeZone dialog failed: ', e);
    }
  }

  getToken() {
    this.integrationService.getJwtToken().subscribe({
      next: response => {
        if (response) {
          this.integrationService.intercomToken$.next(response?.data?.toString());
          window.Intercom('update', {
            app_id: 'm2dfluw7',
            intercom_user_jwt: response?.data?.toString(),
            user_id: this.userData?.useracctid,
            name: this.userData?.fullName,
            email: this.userData?.emailAddress,
            created_at: 140520251000,
          });
          console.log(response.data);
        }
      }
    })
  }
}
